import { useAuthStore } from '../stores/authStore'

export const BASE = import.meta.env.VITE_API_URL ?? ''

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// 'ok' — обновили, 'auth_error' — токен невалиден (нужен логаут),
// 'network_error' — сеть/сервер недоступен (логаут делать нельзя)
type RefreshOutcome = 'ok' | 'auth_error' | 'network_error'

// _retry=true означает, что это повтор после рефреша — больше не повторяем.
async function request<T>(
  path: string,
  init: RequestInit = {},
  _retry = false,
): Promise<T> {
  const token = localStorage.getItem('access_token')
  const isFormData = init.body instanceof FormData
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(init.headers as Record<string, string>),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${BASE}${path}`, { ...init, headers })

  if (res.status === 401 && !_retry) {
    const outcome = await tryRefresh()
    if (outcome === 'ok') return request(path, init, true)  // повторяем только один раз
    // Только при явном отказе сервера — выходим. При сетевой ошибке токены не трогаем.
    if (outcome === 'auth_error') {
      localStorage.clear()
      window.location.href = '/login'
    }
    throw new ApiError(401, 'Unauthorized')
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }))
    throw new ApiError(res.status, body.error ?? res.statusText)
  }

  if (res.status === 204) return undefined as T
  return res.json()
}

// Singleton: все параллельные вызовы ждут одного refresh-запроса.
let _refreshPromise: Promise<RefreshOutcome> | null = null

async function tryRefresh(): Promise<RefreshOutcome> {
  if (_refreshPromise) return _refreshPromise
  _refreshPromise = _doRefresh().finally(() => { _refreshPromise = null })
  return _refreshPromise
}

async function _doRefresh(): Promise<RefreshOutcome> {
  const rt = localStorage.getItem('refresh_token')
  if (!rt) return 'auth_error'
  try {
    const res = await fetch(`${BASE}/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    })
    if (!res.ok) {
      // 401/403 — токен отозван/истёк, нужен логаут; остальное — временная ошибка
      return (res.status === 401 || res.status === 403) ? 'auth_error' : 'network_error'
    }
    const data = await res.json()
    localStorage.setItem('access_token', data.access_token)
    localStorage.setItem('refresh_token', data.refresh_token)
    useAuthStore.getState().setTokens(data.access_token, data.refresh_token)
    return 'ok'
  } catch {
    return 'network_error'
  }
}

export async function tryRefreshToken(): Promise<boolean> {
  return (await tryRefresh()) === 'ok'
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'DELETE', body: body ? JSON.stringify(body) : undefined }),
  upload: <T>(path: string, form: FormData) =>
    request<T>(path, { method: 'POST', body: form }),
}

export { ApiError }
